import { useTranslation } from 'react-i18next';
import { API, useActivePlayers, useSharedProperties } from '@unboared/react';

import { BrandLogo } from '~/components/branding/BrandLogo';
import { GameRating } from '~/components/GameRating';
import { parseRegiePhone, REGIE_PHONE_KEY, RegiePhoneState } from '~/types/screen/regie';

import styles from './PhoneWaitingScreen.module.css';

/**
 * Téléphone : ce que montre l'écran public du double écran (`undefined` hors double
 * écran, ou TV sur une ancienne version). Même lecture protégée que useFreePlayState.
 */
export function useRegiePhone(): RegiePhoneState | undefined {
    const { getSharedProperty } = useSharedProperties();
    try {
        return parseRegiePhone(getSharedProperty(REGIE_PHONE_KEY));
    } catch {
        return undefined; // hôte pas encore connu
    }
}

function myPlayer(): { name: string; avatar?: string } | null {
    try {
        const p = API.getPlayer(API.getDeviceID());
        return p ? { name: p.username || '', avatar: p.avatar } : null;
    } catch {
        return null; // session pas encore synchronisée (gotcha #16)
    }
}

/**
 * Double écran : tant que la TV montre l'accueil de la soirée ou un message, le
 * téléphone attend ici au lieu d'afficher la manette du jeu (qui reste chargée
 * dessous, prête dès le lancement). Le joueur sait qu'il est bien connecté, peut
 * corriger son pseudo et son avatar, lit l'annonce de l'animateur et, après un jeu,
 * note son expérience (même notation que l'écran entre deux jeux, une fois par session).
 */
export function PhoneWaitingScreen({
    state,
    clientID,
    ratedGameID,
    hidden = false,
    onEditProfile,
}: {
    state: RegiePhoneState;
    clientID?: string;
    /** Jeu que la TV vient de montrer : propose de le noter (rien en début de soirée). */
    ratedGameID?: string;
    /** Profil en cours de modification : caché, mais gardé monté (notation en cours conservée). */
    hidden?: boolean;
    onEditProfile: () => void;
}) {
    const { t } = useTranslation();
    const { activePlayers } = useActivePlayers();
    const me = myPlayer();
    const count = activePlayers.length;
    const announce = state.view === 'message' ? state.message.trim() : state.ticker.trim();
    const playing = state.view === 'game';

    // Reste monté pendant le jeu (caché) : la notation commencée sur l'accueil n'est pas
    // perdue, et part au lancement du jeu.
    return (
        <div className={`${styles.screen} ${playing || hidden ? styles.hidden : ''}`}>
            <div className={styles.top}>
                <BrandLogo clientID={clientID} className={styles.logo} />
            </div>

            <div className={styles.head}>
                <span className={styles.kicker}>{t('regie.phone.tonight')}</span>
                <h1 className={styles.title}>{state.title}</h1>
            </div>

            {me && (
                <div className={styles.me}>
                    <span className={styles.avatar}>
                        {me.avatar ? <img src={me.avatar} alt="" /> : me.name.slice(0, 1).toUpperCase()}
                    </span>
                    <span className={styles.meText}>
                        <span className={styles.meLabel}>{t('regie.phone.in')}</span>
                        <span className={styles.meName}>{me.name}</span>
                    </span>
                    <button type="button" className={styles.edit} onClick={onEditProfile}>
                        {t('regie.phone.edit')}
                    </button>
                </div>
            )}

            {announce && (
                <div className={styles.announce}>
                    <span className={styles.announceLabel}>
                        {state.view === 'message' ? t('regie.phone.message') : t('regie.phone.news')}
                    </span>
                    <p className={styles.announceText}>{announce}</p>
                </div>
            )}

            {/* Jeu lancé avant « Envoyer » : les étoiles déjà choisies partent quand même. */}
            <GameRating gameID={ratedGameID} clientID={clientID} className={styles.rating} submitPending={playing} />

            <div className={styles.wait}>
                <span className={styles.dots} aria-hidden="true">
                    <span />
                    <span />
                    <span />
                </span>
                <span>{t('regie.phone.soon')}</span>
                {count > 1 && <span className={styles.count}>{t('regie.phone.players', { count })}</span>}
                <span className={styles.hint}>{t('regie.phone.keepOpen')}</span>
            </div>
        </div>
    );
}
